import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ShoppingBag, 
  Loader2, 
  RefreshCw,
  TrendingUp,
  Download,
  Calendar,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import {
  purchaseDashboardApiService,
  MonthlySummary,
  MonthlySummaryWithCategorySupplier,
  CategoryDetail,
  SupplierDetail,
} from '../services/purchaseDashboardApi';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell
} from 'recharts';

// Theme color for bars - using blue theme
const THEME_COLOR = '#3b82f6'; // blue-500
const THEME_COLOR_SELECTED = '#f97316'; // orange-500 for selected

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type DatePreset = '30d' | '60d' | '90d' | '6m' | '1y' | 'custom';

const DATE_PRESETS: { id: Exclude<DatePreset, 'custom'>; label: string }[] = [
  { id: '30d', label: '30 Days' },
  { id: '60d', label: '60 Days' },
  { id: '90d', label: '90 Days' },
  { id: '6m', label: '6 Months' },
  { id: '1y', label: '1 Year' },
];

function toISODate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayDate(): string {
  return toISODate(new Date());
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getRangeForPreset(preset: Exclude<DatePreset, 'custom'>): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case '30d':
      start.setDate(end.getDate() - 29);
      break;
    case '60d':
      start.setDate(end.getDate() - 59);
      break;
    case '90d':
      start.setDate(end.getDate() - 89);
      break;
    case '6m':
      start.setMonth(end.getMonth() - 6);
      break;
    case '1y':
      start.setFullYear(end.getFullYear() - 1);
      break;
  }
  return { startDate: toISODate(start), endDate: toISODate(end) };
}

function monthOverlapsRange(year: number, month: number, startDate: string, endDate: string): boolean {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  const rangeStart = new Date(startDate);
  const rangeEnd = new Date(endDate);
  if (Number.isNaN(rangeStart.getTime()) || Number.isNaN(rangeEnd.getTime())) return true;
  return monthStart <= rangeEnd && monthEnd >= rangeStart;
}

export function PurchaseDashboard() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  
  const defaultRange = useMemo(() => getRangeForPreset('1y'), []);
  const [loading, setLoading] = useState(false);
  const [detailedRows, setDetailedRows] = useState<MonthlySummaryWithCategorySupplier[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<{ year: number; month: number } | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('ALL');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('ALL');
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [appliedStartDate, setAppliedStartDate] = useState(defaultRange.startDate);
  const [appliedEndDate, setAppliedEndDate] = useState(defaultRange.endDate);
  const [datePreset, setDatePreset] = useState<DatePreset>('1y');

  // Load monthly summary on mount and when branch changes
  useEffect(() => {
    loadMonthlySummary(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  const loadMonthlySummary = async (from = startDate, to = endDate) => {
    if (!from || !to) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (new Date(from) > new Date(to)) {
      toast.error('Start date cannot be after end date');
      return;
    }

    try {
      setLoading(true);
      const branchId = selectedBranch || user?.defaultBranchId;
      const response = await purchaseDashboardApiService.getMonthlySummaryDetailed(
        branchId || undefined,
        from,
        to
      );
      
      if (response && response.success && Array.isArray(response.data)) {
        const rowsInRange = response.data.filter((row) => {
          const year = Number((row as any).year);
          const month = Number((row as any).month);
          if (!year || !month) return true;
          return monthOverlapsRange(year, month, from, to);
        });
        setDetailedRows(rowsInRange);
        setAppliedStartDate(from);
        setAppliedEndDate(to);

        if (rowsInRange.length > 0) {
          const latest = [...rowsInRange].sort((a, b) => {
            const aKey = Number(a.year) * 100 + Number(a.month);
            const bKey = Number(b.year) * 100 + Number(b.month);
            return bKey - aKey;
          })[0];
          setSelectedMonth({ year: Number(latest.year), month: Number(latest.month) });
        } else {
          setSelectedMonth(null);
        }
      } else {
        toast.error('Failed to load monthly summary');
        setDetailedRows([]);
        setSelectedMonth(null);
      }
    } catch (error) {
      // console.error('Error loading monthly summary:', error);
      toast.error('Failed to load monthly purchase summary');
      setDetailedRows([]);
      setSelectedMonth(null);
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (preset: Exclude<DatePreset, 'custom'>) => {
    const range = getRangeForPreset(preset);
    setDatePreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    loadMonthlySummary(range.startDate, range.endDate);
  };

  const handleBarClick = (data: any) => {
    if (data) {
      let year: number, month: number;
      
      // Use monthDate if available, otherwise fallback to year and month
      if (data.monthDate) {
        const date = new Date(data.monthDate);
        year = date.getFullYear();
        month = date.getMonth() + 1;
      } else if (data.year && data.month) {
        year = data.year;
        month = data.month;
      } else {
        return;
      }
      
      setSelectedMonth({ year, month });
    }
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const getRowQuantity = (row: any): number =>
    Number(row?.quantity ?? row?.totalQuantity ?? row?.purchaseQuantity ?? 0) || 0;

  const showQuantity = selectedCategoryId !== 'ALL';

  // Apply global filters (category/supplier) across ALL months
  const filteredRowsAllMonths = useMemo(() => {
    let rows = detailedRows;
    if (selectedCategoryId !== 'ALL') {
      rows = rows.filter((r) => String((r as any).categoryId ?? '') === String(selectedCategoryId));
    }
    if (selectedSupplierId !== 'ALL') {
      rows = rows.filter((r) => String((r as any).supplierId ?? '') === String(selectedSupplierId));
    }
    return rows;
  }, [detailedRows, selectedCategoryId, selectedSupplierId]);

  // Monthly chart data derived from filtered rows (whole dashboard reacts to filters)
  const monthlySummary: MonthlySummary[] = useMemo(() => {
    const monthlyMap = new Map<string, MonthlySummary>();
    filteredRowsAllMonths.forEach((row) => {
      const year = Number((row as any).year);
      const month = Number((row as any).month);
      if (!year || !month) return;
      const key = `${year}-${month}`;
      const prev = monthlyMap.get(key);
      const addValue = Number((row as any).totalPurchaseValue ?? 0) || 0;
      const addCount = Number((row as any).purchaseCount ?? 0) || 0;
      const addQty = getRowQuantity(row);

      if (!prev) {
        monthlyMap.set(key, {
          year,
          month,
          monthDate: (row as any).monthDate,
          monthName: (row as any).monthName,
          totalPurchaseValue: addValue,
          purchaseCount: addCount || undefined,
          quantity: addQty,
        });
      } else {
        prev.totalPurchaseValue = (prev.totalPurchaseValue || 0) + addValue;
        prev.purchaseCount = (Number(prev.purchaseCount ?? 0) + addCount) || prev.purchaseCount;
        prev.quantity = (Number(prev.quantity ?? 0) || 0) + addQty;
      }
    });

    return Array.from(monthlyMap.values())
      .map((item: any) => {
        let displayLabel = '';
        let sortDate: Date;

        if (item.monthDate) {
          sortDate = new Date(item.monthDate);
          const monthName =
            MONTH_NAMES[sortDate.getMonth()] || MONTH_NAMES[item.month - 1] || `M${item.month}`;
          displayLabel = `${monthName} ${sortDate.getFullYear()}`;
        } else {
          sortDate = new Date(item.year, item.month - 1, 1);
          const monthName = MONTH_NAMES[item.month - 1] || `M${item.month}`;
          displayLabel = `${monthName} ${item.year}`;
        }

        return {
          ...item,
          monthName: item.monthName || MONTH_NAMES[item.month - 1] || `M${item.month}`,
          displayLabel,
          sortDate: sortDate.getTime(),
        };
      })
      .sort((a: any, b: any) => (a.sortDate || 0) - (b.sortDate || 0));
  }, [filteredRowsAllMonths]);

  const rowsForSelectedMonth = useMemo(() => {
    if (!selectedMonth) return [];
    return filteredRowsAllMonths.filter(
      (r) => Number((r as any).year) === selectedMonth.year && Number((r as any).month) === selectedMonth.month
    );
  }, [filteredRowsAllMonths, selectedMonth]);

  const categoryDetails: CategoryDetail[] = useMemo(() => {
    const map = new Map<string, CategoryDetail>();
    rowsForSelectedMonth.forEach((r) => {
      const id = (r as any).categoryId;
      const name = (r as any).categoryName ?? 'N/A';
      if (id == null) return;
      const key = String(id);
      const prev = map.get(key);
      const addValue = Number((r as any).totalPurchaseValue ?? 0) || 0;
      const addCount = Number((r as any).purchaseCount ?? 0) || 0;
      const addQty = getRowQuantity(r);
      if (!prev) {
        map.set(key, {
          categoryId: Number(id),
          categoryName: String(name),
          totalPurchaseValue: addValue,
          purchaseCount: addCount || undefined,
          quantity: addQty,
        });
      } else {
        prev.totalPurchaseValue = (prev.totalPurchaseValue || 0) + addValue;
        prev.purchaseCount = (Number(prev.purchaseCount ?? 0) + addCount) || prev.purchaseCount;
        prev.quantity = (Number(prev.quantity ?? 0) || 0) + addQty;
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.categoryName ?? '').localeCompare(String(b.categoryName ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [rowsForSelectedMonth]);

  const supplierDetails: (SupplierDetail & {
    deliveryRating?: number;
    onTimeDeliveryPercentage?: number;
    performanceScore?: number;
  })[] = useMemo(() => {
    const map = new Map<string, SupplierDetail>();
    rowsForSelectedMonth.forEach((r) => {
      const id = (r as any).supplierId;
      const name = (r as any).supplierName ?? 'N/A';
      if (id == null) return;
      const key = String(id);
      const prev = map.get(key);
      const addValue = Number((r as any).totalPurchaseValue ?? 0) || 0;
      const addCount = Number((r as any).purchaseCount ?? 0) || 0;
      const addQty = getRowQuantity(r);
      if (!prev) {
        map.set(key, {
          supplierId: Number(id),
          supplierName: String(name),
          totalPurchaseValue: addValue,
          purchaseCount: addCount || undefined,
          quantity: addQty,
        });
      } else {
        prev.totalPurchaseValue = (prev.totalPurchaseValue || 0) + addValue;
        prev.purchaseCount = (Number(prev.purchaseCount ?? 0) + addCount) || prev.purchaseCount;
        prev.quantity = (Number(prev.quantity ?? 0) || 0) + addQty;
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.supplierName ?? '').localeCompare(String(b.supplierName ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [rowsForSelectedMonth]);

  const categoryTotal = useMemo(() => {
    return categoryDetails.reduce((sum, item) => sum + (item.totalPurchaseValue || 0), 0);
  }, [categoryDetails]);

  const supplierTotal = useMemo(() => {
    return supplierDetails.reduce((sum, item) => sum + (item.totalPurchaseValue || 0), 0);
  }, [supplierDetails]);

  const categoryQtyTotal = useMemo(() => {
    return categoryDetails.reduce((sum, item) => sum + (Number(item.quantity ?? 0) || 0), 0);
  }, [categoryDetails]);

  // Get top 15 suppliers sorted by purchase value (after applying global filters)
  const topSuppliers = useMemo(() => {
    return [...supplierDetails]
      .sort((a, b) => (b.totalPurchaseValue || 0) - (a.totalPurchaseValue || 0))
      .slice(0, 15);
  }, [supplierDetails]);

  const supplierQtyTotal = useMemo(() => {
    return topSuppliers.reduce((sum, item) => sum + (Number(item.quantity ?? 0) || 0), 0);
  }, [topSuppliers]);

  const categoryOptions = useMemo(() => {
    const map = new Map<string, { categoryId: number; categoryName: string }>();
    const baseRows =
      selectedSupplierId !== 'ALL'
        ? detailedRows.filter((r) => String((r as any).supplierId ?? '') === String(selectedSupplierId))
        : detailedRows;
    baseRows.forEach((r) => {
      const id = (r as any).categoryId;
      if (id == null) return;
      const key = String(id);
      if (!map.has(key)) {
        map.set(key, {
          categoryId: Number(id),
          categoryName: String((r as any).categoryName ?? 'N/A'),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.categoryName ?? '').localeCompare(String(b.categoryName ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [detailedRows, selectedSupplierId]);

  const supplierOptions = useMemo(() => {
    const map = new Map<string, { supplierId: number; supplierName: string }>();
    const baseRows =
      selectedCategoryId !== 'ALL'
        ? detailedRows.filter((r) => String((r as any).categoryId ?? '') === String(selectedCategoryId))
        : detailedRows;
    baseRows.forEach((r) => {
      const id = (r as any).supplierId;
      if (id == null) return;
      const key = String(id);
      if (!map.has(key)) {
        map.set(key, {
          supplierId: Number(id),
          supplierName: String((r as any).supplierName ?? 'N/A'),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.supplierName ?? '').localeCompare(String(b.supplierName ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [detailedRows, selectedCategoryId]);

  // Determine whether to show values in Lakhs or Crores on Y axis
  const yAxisTickFormatter = useMemo(() => {
    const maxValue = monthlySummary.reduce(
      (max, item) => Math.max(max, item.totalPurchaseValue || 0),
      0
    );
    // Switch to Cr when value exceeds ₹100L
    const useCrores = maxValue >= 10_000_000;

    return (value: number) => {
      if (useCrores) {
        return `₹${(value / 10_000_000).toFixed(1)}Cr`;
      }
      return `₹${(value / 100_000).toFixed(1)}L`;
    };
  }, [monthlySummary]);

  // Custom tooltip for bar chart
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{data.displayLabel || `${data.monthName} ${data.year}`}</p>
          <p className="text-primary-600">
            Total: {formatCurrency(data.totalPurchaseValue)}
          </p>
          {showQuantity && (
            <p className="text-gray-700 text-sm">
              Quantity: {formatNumber(data.quantity)}
            </p>
          )}
          {data.purchaseCount !== undefined && (
            <p className="text-gray-600 text-sm">Orders: {data.purchaseCount}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Purchase Dashboard</CardTitle>
                <p className="text-gray-600 text-sm">
                  {formatDate(appliedStartDate)} to {formatDate(appliedEndDate)}
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="inline-flex flex-wrap rounded-lg border border-primary-200 bg-white p-1">
                {DATE_PRESETS.map((preset) => (
                  <Button
                    key={preset.id}
                    type="button"
                    size="sm"
                    variant={datePreset === preset.id ? 'default' : 'ghost'}
                    disabled={loading}
                    onClick={() => applyPreset(preset.id)}
                    className={
                      datePreset === preset.id
                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                        : 'text-gray-700 hover:bg-primary-50'
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-3 lg:justify-end">
              <div className="space-y-1">
                <Label htmlFor="purchase-startDate" className="text-xs text-gray-600">From</Label>
                <Input
                  id="purchase-startDate"
                  type="date"
                  value={startDate}
                  max={endDate || getTodayDate()}
                  onChange={(e) => {
                    setDatePreset('custom');
                    setStartDate(e.target.value);
                  }}
                  className="w-40 border-primary-200 bg-white"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="purchase-endDate" className="text-xs text-gray-600">To</Label>
                <Input
                  id="purchase-endDate"
                  type="date"
                  value={endDate}
                  max={getTodayDate()}
                  min={startDate || undefined}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    const today = getTodayDate();
                    setDatePreset('custom');
                    if (selectedDate <= today) {
                      setEndDate(selectedDate);
                    } else {
                      setEndDate(today);
                      toast.error('End date cannot be more than today');
                    }
                  }}
                  className="w-40 border-primary-200 bg-white"
                />
              </div>
              <Button
                onClick={() => loadMonthlySummary()}
                size="sm"
                disabled={loading}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">Apply</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Global filters (apply to details tables) */}
          <div className="flex flex-col gap-3 mb-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary-600" />
              <div>
                <p className="text-sm text-gray-600">Selected Month</p>
                <p className="font-semibold text-gray-800">
                  {selectedMonth
                    ? `${MONTH_NAMES[selectedMonth.month - 1]} ${selectedMonth.year}`
                    : 'Select a month from the chart'}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">Category</span>
                <Select
                  value={selectedCategoryId}
                  onValueChange={setSelectedCategoryId}
                  disabled={loading || detailedRows.length === 0}
                >
                  <SelectTrigger className="w-[240px] bg-white">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search categories..." debounceMs={150}>
                    <SelectItem value="ALL">All categories</SelectItem>
                    {categoryOptions.map((c) => (
                      <SelectItem key={c.categoryId} value={String(c.categoryId)}>
                        {c.categoryName || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">Supplier</span>
                <Select
                  value={selectedSupplierId}
                  onValueChange={setSelectedSupplierId}
                  disabled={loading || detailedRows.length === 0}
                >
                  <SelectTrigger className="w-[260px] bg-white">
                    <SelectValue placeholder="All suppliers" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search suppliers..." debounceMs={150}>
                    <SelectItem value="ALL">All suppliers</SelectItem>
                    {supplierOptions.map((s) => (
                      <SelectItem key={s.supplierId} value={String(s.supplierId)}>
                        {s.supplierName || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={loading || detailedRows.length === 0}
                onClick={() => {
                  setSelectedCategoryId('ALL');
                  setSelectedSupplierId('ALL');
                }}
              >
                Clear
              </Button>
            </div>
          </div>

          {/* Monthly Summary Bar Chart */}
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
              <p className="text-gray-600">Loading monthly purchase summary...</p>
            </div>
          ) : monthlySummary.length > 0 ? (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary-600" />
                Monthly Purchase Summary (Last 12 Months)
              </h3>
              <div className="bg-white p-4 rounded-lg border border-primary-200">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={monthlySummary}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="displayLabel" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      tickFormatter={yAxisTickFormatter}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="totalPurchaseValue" 
                      onClick={handleBarClick}
                      style={{ cursor: 'pointer' }}
                    >
                      {monthlySummary.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={
                            selectedMonth && 
                            entry.year === selectedMonth.year && 
                            entry.month === selectedMonth.month
                              ? THEME_COLOR_SELECTED
                              : THEME_COLOR
                          } 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Click on a bar to view detailed breakdown by category and supplier
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No purchase data available</p>
              <p className="text-gray-400 text-sm mt-2">
                No purchase data found for the selected period
              </p>
            </div>
          )}

          {/* Category and Supplier Details Tables */}
          {selectedMonth && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Breakdown</h3>

              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
                  <p className="text-gray-600">Loading purchase details...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Category Details Table */}
                  <Card className="border border-primary-200">
                    <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">By Category</CardTitle>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-lg font-semibold text-primary-600">{formatCurrency(categoryTotal)}</p>
                          {showQuantity && (
                            <p className="text-xs text-gray-600">Qty: {formatNumber(categoryQtyTotal)}</p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {categoryDetails.length > 0 ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead>Category</TableHead>
                                {showQuantity && <TableHead className="text-right">Quantity</TableHead>}
                                <TableHead className="text-right">Purchase Value</TableHead>
                                <TableHead className="text-right">Percentage</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {categoryDetails.map((item, index) => {
                                const percentage = categoryTotal > 0 
                                  ? ((item.totalPurchaseValue / categoryTotal) * 100).toFixed(1)
                                  : '0.0';
                                return (
                                  <TableRow key={item.categoryId || index} className="hover:bg-primary-50/50">
                                    <TableCell className="font-medium">
                                      {item.categoryName || 'N/A'}
                                    </TableCell>
                                    {showQuantity && (
                                      <TableCell className="text-right text-gray-700">
                                        {formatNumber(item.quantity)}
                                      </TableCell>
                                    )}
                                    <TableCell className="text-right font-semibold text-primary-600">
                                      {formatCurrency(item.totalPurchaseValue)}
                                    </TableCell>
                                    <TableCell className="text-right text-gray-600">
                                      {percentage}%
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          No category data for this filter
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Supplier Details Table */}
                  <Card className="border border-primary-200">
                    <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">By Supplier (Top 15)</CardTitle>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-lg font-semibold text-primary-600">{formatCurrency(supplierTotal)}</p>
                          {showQuantity && (
                            <p className="text-xs text-gray-600">Qty: {formatNumber(supplierQtyTotal)}</p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {topSuppliers.length > 0 ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead>Supplier</TableHead>
                                {showQuantity && <TableHead className="text-right">Quantity</TableHead>}
                                <TableHead className="text-right">Purchase Value</TableHead>
                                <TableHead className="text-right">Percentage</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {topSuppliers.map((item, index) => {
                                const percentage =
                                  supplierTotal > 0
                                    ? ((item.totalPurchaseValue / supplierTotal) * 100).toFixed(1)
                                    : '0.0';
                                
                                return (
                                  <TableRow key={item.supplierId || index} className="hover:bg-primary-50/50">
                                    <TableCell className="font-medium">
                                      {item.supplierName || 'N/A'}
                                    </TableCell>
                                    {showQuantity && (
                                      <TableCell className="text-right text-gray-700">
                                        {formatNumber(item.quantity)}
                                      </TableCell>
                                    )}
                                    <TableCell className="text-right font-semibold text-primary-600">
                                      {formatCurrency(item.totalPurchaseValue)}
                                    </TableCell>
                                    <TableCell className="text-right text-gray-600">{percentage}%</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          No supplier data available for this month
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

